/* TextEditorComponent.js */

"use strict";

const _ = require("lodash");
const InputBaseControlTextfieldComponent = require("@gluenetworks/input_base_control").Textfield;
const TextEditorRenderer = require("./TextEditorRenderer");
const DEFAULT_REQUIRED_VIOLATION_MESSAGE = "Please enter a value.";

class TextEditorComponent extends InputBaseControlTextfieldComponent {
    /**
     * @schema.name Text Editor Control
     * @schema.package @gluenetworks/text_editor
     * @schema.packageIdentifier TextEditorComponent
     * @schema.namespace require("@gluenetworks/text_editor").TextEditorComponent
     *
     * @schema.configuration.languageMode.name languageMode
     * @schema.configuration.languageMode.type String
     * @schema.configuration.languageMode.default 'ace/mode/text'
     * @schema.configuration.languageMode.required false
     *
     * @schema.configuration.showLineNumbers.name showLineNumbers
     * @schema.configuration.showLineNumbers.type Boolean
     * @schema.configuration.showLineNumbers.default true
     * @schema.configuration.showLineNumbers.required false
     *
     * @schema.configuration.showHiddenText.name showHiddenText
     * @schema.configuration.showHiddenText.type Boolean
     * @schema.configuration.showHiddenText.default false
     * @schema.configuration.showHiddenText.required false
     *
     * @schema.configuration.validationVerbiage.name validationVerbiage
     * @schema.configuration.validationVerbiage.type object
     * @schema.configuration.validationVerbiage.default null
     * @schema.configuration.validationVerbiage.required false
     *
     * @schema.configuration.tabSize.name tabSize
     * @schema.configuration.tabSize.type Number
     * @schema.configuration.tabSize.default 4
     * @schema.configuration.tabSize.required false
     *
     * @schema.configuration.minLineCount.name minLineCount
     * @schema.configuration.minLineCount.type Number
     * @schema.configuration.minLineCount.default 10
     * @schema.configuration.minLineCount.required false
     *
     * @schema.configuration.maxLineCount.name maxLineCount
     * @schema.configuration.maxLineCount.type Number
     * @schema.configuration.maxLineCount.default Infinity
     * @schema.configuration.maxLineCount.required false
     *
     * @schema.configuration.autoResize.name autoResize
     * @schema.configuration.autoResize.type boolean
     * @schema.configuration.autoResize.default false
     * @schema.configuration.autoResize.required false
     **/
    constructor(config) {
        super(config);
        this._autoResize = _.get(config, "autoResize", false);
        this._languageMode = _.get(config, "languageMode", "ace/mode/text");
        this._minLineCount = _.get(config, "minLineCount", 10);
        this._maxLineCount = _.get(config, "maxLineCount", Infinity);
        this._showLineNumbers = _.get(config, "showLineNumbers", true);
        this._showHiddenText = _.get(config, "showHiddenText", false);
        this._tabSize = _.get(config, "tabSize", 4);
        this._theme = _.get(config, "theme", "ace/theme/chrome");
        let validationVerbiage = _.get(config, "validationVerbiage", {});
        this._validationVerbiage = this._buildValidationVerbiage(validationVerbiage);
        this._renderer = new TextEditorRenderer(this);
    }

    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}

    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/

    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }

    shutdown() {
        this._unregisterListeners();
        if (!this.renderer) {
            return Promise.resolve();
        } else {
            return this._renderer.shutdown().then(
                () => {
                    this._renderer = undefined;
                    this._disabled = undefined;
                    this._invalid = undefined;
                    this._visible = undefined;
                    this._value = undefined;
                    this._languageMode = undefined;
                    this._darkTheme = undefined;
                    this._theme = undefined;
                    this._placeholder = undefined;
                    this._autocompleteHint = undefined;
                    this._patternMatchAll = undefined;
                    this._pattern = undefined;
                    this._useNativeValidation = undefined;
                    this._showErrorMessage = undefined;
                    this._required = undefined;
                    this._validationVerbiage = undefined;
                    this._languageMode = undefined;
                    this._showLineNumbers = undefined;
                },
                () => {
                    //Error handling on renderer shut down
                }
            );
        }
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /**
     * Returns validation messages verbiage
     * @param validationVerbiage
     * @returns {any}
     * @private
     */
    _buildValidationVerbiage(validationVerbiage) {
        let defaultValidationVerbiage = {};
        // default pattern violation message verbiage makes use of pattern value, hence we set it only if pattern attribute is set
        defaultValidationVerbiage["required"] = DEFAULT_REQUIRED_VIOLATION_MESSAGE;
        return _.isEmpty(validationVerbiage)
            ? defaultValidationVerbiage
            : Object.assign(defaultValidationVerbiage, validationVerbiage);
    }

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.setLanguageMode.name setLanguageMode
     * @schema.methods.setLanguageMode.description set language mode displayed on logs UI.
     *  Options - javascript, json etc
     * @schema.methods.setLanguageMode.returns Instance of the control
     * @schema.methods.setLanguageMode.parameters.newMode.name newMode
     * @schema.methods.setLanguageMode.parameters.newMode.description new language mode selection
     * @schema.methods.setLanguageMode.parameters.newMode.type String
     */
    setLanguageMode(newLanguageMode) {
        if (newLanguageMode !== this._languageMode) {
            this._languageMode = newLanguageMode;
            this.emit("LanguageMode", this._languageMode);
        }
        return this;
    }

    /**
     * @schema.methods.getLanguageMode.name getLanguageMode
     * @schema.methods.getLanguageMode.description Returns language mode of text editor
     * @schema.methods.getLanguageMode.returns String
     */
    getLanguageMode() {
        return this._languageMode;
    }

    get languageMode() {
        return this.getLanguageMode();
    }

    set languageMode(newLanguageMode) {
        return this.setLanguageMode(newLanguageMode);
    }

    /**
     * @schema.methods.setShowLineNumbers.name setShowLineNumbers
     * @schema.methods.setShowLineNumbers.description show or hide line numbers on editor gutter
     * @schema.methods.setShowLineNumbers.returns Instance of the control
     * @schema.methods.setShowLineNumbers.parameters.showLineNumbers.name showLineNumbers
     * @schema.methods.setShowLineNumbers.parameters.showLineNumbers.description show/hide line numbers on gutter
     * @schema.methods.setShowLineNumbers.parameters.showLineNumbers.type Boolean
     */
    setShowLineNumbers(showLineNumbers) {
        if (showLineNumbers !== this._showLineNumbers) {
            this._showLineNumbers = showLineNumbers === true;
            this.emit("ShowLineNumbers", this._showLineNumbers);
        }
        return this;
    }

    /**
     * @schema.methods.getShowLineNumbers.name getShowLineNumbers
     * @schema.methods.getShowLineNumbers.description Returns if gutter is displayed on text editor
     * @schema.methods.getShowLineNumbers.returns Boolean
     */
    getShowLineNumbers() {
        return this._showLineNumbers;
    }

    get showLineNumbers() {
        return this.getShowLineNumbers();
    }

    set showLineNumbers(newShowLineNumbers) {
        return this.setShowLineNumbers(newShowLineNumbers);
    }

    /**
     * @schema.methods.setShowHiddenText.name setShowHiddenText
     * @schema.methods.setShowHiddenText.description show or hide hidden text on the editor
     * @schema.methods.setShowHiddenText.returns Instance of the control
     * @schema.methods.setShowHiddenText.parameters.showHiddenText.name showHiddenText
     * @schema.methods.setShowHiddenText.parameters.showHiddenText.description show/hide hidden text
     * @schema.methods.setShowHiddenText.parameters.showHiddenText.type Boolean
     */
    setShowHiddenText(showHiddenText) {
        if (showHiddenText !== this._showHiddenText) {
            this._showHiddenText = showHiddenText === true;
            this.emit("ShowHiddenText", this._showHiddenText);
        }
        return this;
    }

    /**
     * @schema.methods.getShowHiddenText.name getShowHiddenText
     * @schema.methods.getShowHiddenText.description Returns true if hidden text is shown on editor else returns false
     * @schema.methods.getShowHiddenText.returns Boolean
     */
    getShowHiddenText() {
        return this._showHiddenText;
    }

    get showHiddenText() {
        return this.getShowHiddenText();
    }

    set showHiddenText(newShowHiddenText) {
        return this.setShowHiddenText(newShowHiddenText);
    }

    /**
     * @schema.methods.getValidationMessages.name getValidationMessages
     * @schema.methods.getValidationMessages.description Returns list of validation violations. For Example: returns validation messages when the text value doesn't
     *          match given regular expression (pattern) or is left blank when it is required
     * @schema.methods.getValidationMessages.returns {Array}
     */
    getValidationMessages() {
        return this._renderer.getViolations().slice(0);
    }

    get validationMessages() {
        return this.getValidationMessages();
    }

    /**
     * @schema.methods.setValidationVerbiage.name setValidationVerbiage
     * @schema.methods.setValidationVerbiage.description Changes the validation messages verbiage displayed in case of validation
     *                          errors on this control. For example control value doesn't match pattern or is left blank when it is required
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.name validationVerbiage
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.description validation messages displayed on the control when there are
     *                          validation errors on the control value
     * @schema.methods.setValidationVerbiage.parameters.validationVerbiage.type object
     * @schema.methods.setValidationVerbiage.returns Instance of the control
     * @returns {*}
     */
    setValidationVerbiage(validationVerbiage) {
        if (
            _.isEmpty(validationVerbiage) ||
            _.isEqual(this._validationVerbiage, validationVerbiage)
        ) {
            return;
        }
        Object.assign(this._validationVerbiage, validationVerbiage);
        this.emit("ValidationVerbiage", this._validationVerbiage);
        return this;
    }

    /**
     * @schema.methods.getValidationVerbiage.name getValidationVerbiage
     * @schema.methods.getValidationVerbiage.description Returns existing validation message verbiage
     * @schema.methods.getValidationVerbiage.returns {object}
     */
    getValidationVerbiage() {
        return this._validationVerbiage;
    }

    get validationVerbiage() {
        return this._validationVerbiage;
    }

    set validationVerbiage(validationVerbiage) {
        return this.setValidationVerbiage(validationVerbiage);
    }

    /**
     * @schema.methods.setTabSize.name setTabSize
     * @schema.methods.setTabSize.description set tab size on the editor
     * @schema.methods.setTabSize.returns Instance of the control
     * @schema.methods.setTabSize.parameters.tabSize.name setTabSize
     * @schema.methods.setTabSize.parameters.tabSize.description set tab size
     * @schema.methods.setTabSize.parameters.tabSize.type Number
     */
    setTabSize(tabSize) {
        if (tabSize !== this._tabSize && _.isNumber(tabSize)) {
            this._tabSize = tabSize;
            this.emit("TabSize", this._tabSize);
        }
        return this;
    }

    /**
     * @schema.methods.getTabSize.name getTabSize
     * @schema.methods.getTabSize.description Returns tab size
     * @schema.methods.getTabSize.returns Number
     */
    getTabSize() {
        return this._tabSize;
    }

    get tabSize() {
        return this.getTabSize();
    }

    set tabSize(newTabSize) {
        return this.setTabSize(newTabSize);
    }

    /**
     * @schema.methods.getMinLineCount.name getMinLineCount
     * @schema.methods.getMinLineCount.description Returns min lines displayed on editor
     * @schema.methods.getMinLineCount.returns Number
     */
    getMinLineCount() {
        return this._minLineCount;
    }

    get minLineCount() {
        return this.getMinLineCount();
    }

    /**
     * @schema.methods.getMaxLineCount.name getMaxLineCount
     * @schema.methods.getMaxLineCount.description Returns max lines displayed on editor
     * @schema.methods.getMaxLineCount.returns Number
     */
    getMaxLineCount() {
        return this._maxLineCount;
    }

    get maxLineCount() {
        return this.getMaxLineCount();
    }

    /**
     * @schema.methods.setAutoResize.name setAutoResize
     * @schema.methods.setAutoResize.description if true, auto resizes the regex editor
     * @schema.methods.setAutoResize.parameters.newAutoResize.name newAutoResize
     * @schema.methods.setAutoResize.parameters.newAutoResize.description New auto resize flag
     * @schema.methods.setAutoResize.parameters.newAutoResize.type {boolean}
     */
    setAutoResize(newAutoResize) {
        if (this._autoResize !== newAutoResize && _.isBoolean(newAutoResize)) {
            this._autoResize = newAutoResize;
            this.emit("AutoResize", this._autoResize);
        }
        return this;
    }

    /**
     * @schema.methods.getAutoResize.name getAutoResize
     * @schema.methods.getAutoResize.description Returns a flag indicating if the regex editor auto resizes based on the height of its root element
     * @schema.methods.getAutoResize.returns {boolean}
     */
    getAutoResize() {
        return this._autoResize;
    }

    get autoResize() {
        return this.getAutoResize();
    }

    set autoResize(newAutoResize) {
        return this.setAutoResize(newAutoResize);
    }
    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}

module.exports = TextEditorComponent;
